import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, PieChart, Info, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line } from 'recharts';

const STORAGE_KEY = 'profit_margin_data';

export const ProfitMarginCalculator = () => {
  const [revenue, setRevenue] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).revenue : 1000000;
  });
  
  const [cogs, setCogs] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).cogs : 400000;
  });
  
  const [operatingExpenses, setOperatingExpenses] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).operatingExpenses : 350000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      revenue,
      cogs,
      operatingExpenses
    }));
  }, [revenue, cogs, operatingExpenses]);

  const calculateMargins = () => {
    const rev = parseFloat(revenue) || 0;
    const cogsVal = parseFloat(cogs) || 0;
    const opex = parseFloat(operatingExpenses) || 0;

    const grossProfit = rev - cogsVal;
    const operatingProfit = grossProfit - opex;
    const netProfit = operatingProfit; // Simplified (excluding tax/interest)

    const grossMargin = rev > 0 ? (grossProfit / rev) * 100 : 0;
    const operatingMargin = rev > 0 ? (operatingProfit / rev) * 100 : 0;
    const netMargin = rev > 0 ? (netProfit / rev) * 100 : 0;

    return {
      grossProfit,
      operatingProfit,
      netProfit,
      grossMargin,
      operatingMargin,
      netMargin
    };
  };

  const generateMarginComparison = () => {
    const results = calculateMargins();
    
    return [
      { name: 'Gross Margin', value: results.grossMargin, color: 'hsl(var(--chart-1))' },
      { name: 'Operating Margin', value: results.operatingMargin, color: 'hsl(var(--chart-2))' },
      { name: 'Net Margin', value: results.netMargin, color: 'hsl(var(--chart-3))' }
    ];
  };

  const generateCostStructure = () => {
    const cogsVal = parseFloat(cogs) || 0;
    const opex = parseFloat(operatingExpenses) || 0;
    const results = calculateMargins();
    
    return [
      { name: 'COGS', value: cogsVal, color: 'hsl(var(--chart-1))' },
      { name: 'Operating Expenses', value: opex, color: 'hsl(var(--chart-2))' },
      { name: 'Net Profit', value: Math.max(0, results.netProfit), color: 'hsl(var(--chart-3))' }
    ];
  };

  const generateProfitBreakdown = () => {
    const rev = parseFloat(revenue) || 0;
    const results = calculateMargins();
    
    return [
      { 
        name: 'Revenue',
        grossProfit: results.grossProfit,
        operatingProfit: results.operatingProfit,
        netProfit: results.netProfit
      }
    ];
  };

  const generateRevenueScenarios = () => {
    const baseRevenue = parseFloat(revenue) || 0;
    const cogsVal = parseFloat(cogs) || 0;
    const opex = parseFloat(operatingExpenses) || 0;
    const cogsPercent = baseRevenue > 0 ? cogsVal / baseRevenue : 0;

    const scenarios = [0.7, 0.85, 1.0, 1.15, 1.3];
    
    return scenarios.map(multiplier => {
      const scenarioRevenue = baseRevenue * multiplier;
      const scenarioCogs = scenarioRevenue * cogsPercent;
      const scenarioGrossProfit = scenarioRevenue - scenarioCogs;
      const scenarioOperatingProfit = scenarioGrossProfit - opex;
      const scenarioNetMargin = scenarioRevenue > 0 ? (scenarioOperatingProfit / scenarioRevenue) * 100 : 0;
      
      return {
        scenario: `${(multiplier * 100).toFixed(0)}%`,
        revenue: Math.round(scenarioRevenue),
        netMargin: scenarioNetMargin.toFixed(1)
      };
    });
  };

  const generateCostReductionImpact = () => {
    const rev = parseFloat(revenue) || 0;
    const cogsVal = parseFloat(cogs) || 0;
    const opex = parseFloat(operatingExpenses) || 0;

    const reductions = [0, 5, 10, 15, 20];
    
    return reductions.map(reduction => {
      const reducedCogs = cogsVal * (1 - reduction / 100);
      const reducedOpex = opex * (1 - reduction / 100);
      const grossProfit = rev - reducedCogs;
      const operatingProfit = grossProfit - reducedOpex;
      const netMargin = rev > 0 ? (operatingProfit / rev) * 100 : 0;
      
      return {
        reduction: `${reduction}%`,
        netMargin: netMargin.toFixed(1)
      };
    });
  };

  const generateIndustryComparison = () => {
    const results = calculateMargins();
    
    // Industry benchmarks (examples)
    const industries = [
      { name: 'Your Business', grossMargin: results.grossMargin, netMargin: results.netMargin },
      { name: 'Software/SaaS', grossMargin: 75, netMargin: 20 },
      { name: 'Retail', grossMargin: 35, netMargin: 5 },
      { name: 'Manufacturing', grossMargin: 30, netMargin: 8 },
      { name: 'Professional Services', grossMargin: 50, netMargin: 15 }
    ];
    
    return industries;
  };

  const results = calculateMargins();
  const marginComparison = generateMarginComparison();
  const costStructure = generateCostStructure();
  const profitBreakdown = generateProfitBreakdown();
  const revenueScenarios = generateRevenueScenarios();
  const costReductionImpact = generateCostReductionImpact();
  const industryComparison = generateIndustryComparison();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple flex items-center justify-center">
                  <PieChart className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Profit Margin Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-purple data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-purple data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-purple data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What are Profit Margins?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Profit margins measure profitability at different stages of your business operations. They show what percentage 
                  of revenue remains as profit after accounting for various costs. Understanding these metrics is crucial for 
                  pricing strategy, cost management, and business health assessment.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your financial data</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="revenue" className="text-sm font-medium">
                      Total Revenue ($)
                    </Label>
                    <Input
                      id="revenue"
                      type="number"
                      value={revenue}
                      onChange={(e) => setRevenue(e.target.value)}
                      placeholder="1000000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Total sales revenue</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cogs" className="text-sm font-medium">
                      Cost of Goods Sold - COGS ($)
                    </Label>
                    <Input
                      id="cogs"
                      type="number"
                      value={cogs}
                      onChange={(e) => setCogs(e.target.value)}
                      placeholder="400000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Direct costs to produce goods/services</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="operatingExpenses" className="text-sm font-medium">
                      Operating Expenses ($)
                    </Label>
                    <Input
                      id="operatingExpenses"
                      type="number"
                      value={operatingExpenses}
                      onChange={(e) => setOperatingExpenses(e.target.value)}
                      placeholder="350000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Salaries, rent, marketing, admin costs</p>
                  </div>

                  <Button 
                    className="w-full bg-purple text-white hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setRevenue(revenue)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Profit Margin Analysis</CardTitle>
                    <CardDescription>Your profitability metrics at each level</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-3 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <PieChart className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Gross Margin</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {results.grossMargin.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{formatCurrency(results.grossProfit)}</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Operating Margin</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {results.operatingMargin.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{formatCurrency(results.operatingProfit)}</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Margin</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.netMargin.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{formatCurrency(results.netProfit)}</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Revenue</span>
                            <span className="text-sm font-semibold text-foreground">{formatCurrency(parseFloat(revenue))}</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-3">
                            <div 
                              className="bg-primary h-3 rounded-full" 
                              style={{ width: '100%' }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Gross Profit</span>
                            <span className="text-sm font-semibold text-foreground">{formatCurrency(results.grossProfit)} ({results.grossMargin.toFixed(1)}%)</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-3">
                            <div 
                              className="bg-secondary h-3 rounded-full" 
                              style={{ width: `${results.grossMargin}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Operating Profit</span>
                            <span className="text-sm font-semibold text-foreground">{formatCurrency(results.operatingProfit)} ({results.operatingMargin.toFixed(1)}%)</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-3">
                            <div 
                              className="bg-accent h-3 rounded-full" 
                              style={{ width: `${Math.max(0, results.operatingMargin)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Net Profit</span>
                            <span className="text-sm font-semibold text-foreground">{formatCurrency(results.netProfit)} ({results.netMargin.toFixed(1)}%)</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-3">
                            <div 
                              className="bg-purple h-3 rounded-full" 
                              style={{ width: `${Math.max(0, results.netMargin)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Margin Interpretation</CardTitle>
                    <CardDescription>
                      What your margins indicate about business health
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">Gross Margin: {results.grossMargin.toFixed(1)}%</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            results.grossMargin > 50 ? 'bg-secondary/20 text-secondary' :
                            results.grossMargin > 30 ? 'bg-accent/20 text-accent' :
                            'bg-destructive/20 text-destructive'
                          }`}>
                            {results.grossMargin > 50 ? 'Excellent' :
                             results.grossMargin > 30 ? 'Good' :
                             'Needs Improvement'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {results.grossMargin > 50 ? 
                            'Strong pricing power and efficient production. You have room to invest in growth.' :
                           results.grossMargin > 30 ?
                            'Healthy margin, but watch for cost creep. Look for efficiency improvements.' :
                            'Thin margin. Review pricing strategy and production costs urgently.'}
                        </p>
                      </div>
                      
                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">Operating Margin: {results.operatingMargin.toFixed(1)}%</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            results.operatingMargin > 20 ? 'bg-secondary/20 text-secondary' :
                            results.operatingMargin > 10 ? 'bg-accent/20 text-accent' :
                            results.operatingMargin > 0 ? 'bg-destructive/20 text-destructive' :
                            'bg-destructive/20 text-destructive'
                          }`}>
                            {results.operatingMargin > 20 ? 'Excellent' :
                             results.operatingMargin > 10 ? 'Good' :
                             results.operatingMargin > 0 ? 'Weak' :
                             'Negative'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {results.operatingMargin > 20 ? 
                            'Exceptional operational efficiency. Strong competitive position.' :
                           results.operatingMargin > 10 ?
                            'Solid operations. Continue optimizing overhead costs.' :
                           results.operatingMargin > 0 ?
                            'Operating expenses are too high relative to gross profit. Cut overhead.' :
                            'Operating loss. Immediate cost restructuring needed.'}
                        </p>
                      </div>

                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">Net Margin: {results.netMargin.toFixed(1)}%</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            results.netMargin > 15 ? 'bg-secondary/20 text-secondary' :
                            results.netMargin > 5 ? 'bg-accent/20 text-accent' :
                            results.netMargin > 0 ? 'bg-destructive/20 text-destructive' :
                            'bg-destructive/20 text-destructive'
                          }`}>
                            {results.netMargin > 15 ? 'Excellent' :
                             results.netMargin > 5 ? 'Adequate' :
                             results.netMargin > 0 ? 'Minimal' :
                             'Loss'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {results.netMargin > 15 ? 
                            'Outstanding profitability. Strong foundation for growth and reinvestment.' :
                           results.netMargin > 5 ?
                            'Acceptable, but improvement possible. Focus on margin expansion.' :
                           results.netMargin > 0 ?
                            'Barely profitable. Small margin for error. Improve efficiency immediately.' :
                            'Losing money. Fundamental business model review required.'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Margin Comparison</CardTitle>
                <CardDescription className="text-base">
                  Visual breakdown of profitability at each level
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={marginComparison}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Margin (%)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => `${value.toFixed(1)}%`}
                      />
                      <Bar dataKey="value" name="Margin %">
                        {marginComparison.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Cost Structure Breakdown</CardTitle>
                  <CardDescription>
                    Where your revenue goes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={costStructure}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {costStructure.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Industry Comparison</CardTitle>
                  <CardDescription>
                    How you compare to industry averages
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={industryComparison} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          type="number"
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Margin (%)', position: 'insideBottom', offset: -5 }}
                        />
                        <YAxis 
                          type="category"
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          width={120}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => `${value.toFixed(1)}%`}
                        />
                        <Legend />
                        <Bar dataKey="grossMargin" fill="hsl(var(--primary))" name="Gross Margin" />
                        <Bar dataKey="netMargin" fill="hsl(var(--purple))" name="Net Margin" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Revenue Growth Impact</CardTitle>
                  <CardDescription>
                    How scaling affects net margin
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={revenueScenarios}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="scenario" 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Revenue Scale', position: 'insideBottom', offset: -5 }}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Net Margin (%)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="netMargin" 
                          stroke="hsl(var(--secondary))" 
                          strokeWidth={3}
                          name="Net Margin %"
                          dot={{ fill: 'hsl(var(--secondary))' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Cost Reduction Impact</CardTitle>
                  <CardDescription>
                    Effect of cost savings on net margin
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={costReductionImpact}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="reduction" 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Cost Reduction', position: 'insideBottom', offset: -5 }}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Net Margin (%)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="netMargin" 
                          stroke="hsl(var(--accent))" 
                          strokeWidth={3}
                          name="Net Margin %"
                          dot={{ fill: 'hsl(var(--accent))' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Profit Margins</CardTitle>
                <CardDescription className="text-base">
                  Learn what each margin means and how to improve profitability
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    The Three Key Margins Explained
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📊 Gross Margin: {results.grossMargin.toFixed(1)}%</h4>
                      <p className="text-muted-foreground mb-2">
                        <strong>Formula:</strong> (Revenue - COGS) / Revenue × 100
                      </p>
                      <p className="text-muted-foreground mb-2">
                        <strong>What it shows:</strong> How efficiently you produce/deliver your product or service
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Your numbers:</strong> ({formatCurrency(parseFloat(revenue))} - {formatCurrency(parseFloat(cogs))}) / {formatCurrency(parseFloat(revenue))} = {results.grossMargin.toFixed(1)}%
                      </p>
                    </div>

                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📈 Operating Margin: {results.operatingMargin.toFixed(1)}%</h4>
                      <p className="text-muted-foreground mb-2">
                        <strong>Formula:</strong> (Gross Profit - Operating Expenses) / Revenue × 100
                      </p>
                      <p className="text-muted-foreground mb-2">
                        <strong>What it shows:</strong> Profitability after all operating costs (before interest & tax)
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Your numbers:</strong> ({formatCurrency(results.grossProfit)} - {formatCurrency(parseFloat(operatingExpenses))}) / {formatCurrency(parseFloat(revenue))} = {results.operatingMargin.toFixed(1)}%
                      </p>
                    </div>

                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">💰 Net Margin: {results.netMargin.toFixed(1)}%</h4>
                      <p className="text-muted-foreground mb-2">
                        <strong>Formula:</strong> Net Profit / Revenue × 100
                      </p>
                      <p className="text-muted-foreground mb-2">
                        <strong>What it shows:</strong> Bottom line - what % of each dollar you actually keep
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Your numbers:</strong> {formatCurrency(results.netProfit)} / {formatCurrency(parseFloat(revenue))} = {results.netMargin.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Why Margins Matter More Than Revenue
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    A common mistake: focusing only on top-line revenue growth while margins shrink. 
                    You can have $10M in revenue but go bankrupt if margins are negative.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                    <div className="grid sm:grid-cols-2 gap-6 mb-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Company A</p>
                        <p className="text-lg font-semibold text-foreground">$1M Revenue</p>
                        <p className="text-sm text-muted-foreground">30% Net Margin</p>
                        <p className="text-2xl font-bold text-secondary mt-2">$300k Profit</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Company B</p>
                        <p className="text-lg font-semibold text-foreground">$3M Revenue</p>
                        <p className="text-sm text-muted-foreground">5% Net Margin</p>
                        <p className="text-2xl font-bold text-destructive mt-2">$150k Profit</p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-secondary">
                      Company A makes 2x more profit with 1/3 the revenue. Margins matter!
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Pricing Efficiency Check
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your gross margin of {results.grossMargin.toFixed(1)}% indicates your pricing power and production efficiency.
                  </p>
                  <div className="space-y-3">
                    {results.grossMargin < 30 && (
                      <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                        <p className="text-sm font-semibold text-destructive mb-2">⚠️ Low Gross Margin Alert</p>
                        <p className="text-sm text-muted-foreground">
                          At under 30%, you have little room for operating expenses or profit. Consider:
                          <br/>• Raising prices (test with 10-15% increase)
                          <br/>• Reducing COGS through better suppliers or efficiency
                          <br/>• Focusing on higher-margin products/services
                        </p>
                      </div>
                    )}
                    {results.operatingMargin < results.grossMargin * 0.3 && (
                      <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                        <p className="text-sm font-semibold text-destructive mb-2">⚠️ High Operating Expenses</p>
                        <p className="text-sm text-muted-foreground">
                          Your operating expenses consume {((results.grossMargin - results.operatingMargin) / results.grossMargin * 100).toFixed(0)}% of your gross profit. 
                          Target: Keep opex under 70% of gross profit. Review overhead, staffing, and discretionary spending.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Common Margin Mistakes
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Not Tracking COGS Accurately:</strong> Including costs that should be 
                        in COGS as operating expenses (or vice versa) distorts your gross margin.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Aggressive Discounting:</strong> Cutting prices 20% requires 2x sales 
                        volume just to maintain the same profit (at 50% gross margin).
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Ignoring Product Mix:</strong> Different products have different margins. 
                        Selling more low-margin items tanks overall profitability.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Fixed Cost Creep:</strong> Operating expenses tend to grow with revenue, 
                        but should grow slower to improve operating leverage.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Strategies to Improve Margins
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Value-Based Pricing</h4>
                      <p className="text-muted-foreground">
                        Price based on customer value received, not cost-plus. Premium positioning can double margins.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Negotiate Supplier Terms</h4>
                      <p className="text-muted-foreground">
                        Bulk purchasing, longer payment terms, or alternative suppliers can reduce COGS by 10-30%.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Automate & Streamline</h4>
                      <p className="text-muted-foreground">
                        Invest in technology to reduce labor costs and improve efficiency. Initial cost pays back in margin expansion.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Cut Low-Margin Products</h4>
                      <p className="text-muted-foreground">
                        Ruthlessly eliminate products/services with margins below breakeven. Focus on your best performers.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfitMarginCalculator;
